
import { defineComponent } from "vue";
import { getModule } from "vuex-module-decorators";
import digitalAssetUploadService from '../../services/DigitalAssetUploadService';
import PageDirtyService from '../../services/PageDirtyService';

// Icons ---
import {mdiFullscreen, mdiPencil, mdiOpenInNew, mdiArrowUp, mdiRefresh, mdiAlertCircle } from '@mdi/js';

// Components ---
import Icon from '../Icon.vue';
import DraggableModal from '../DraggableModal.vue';
import Lightbox from '../Lightbox.vue';
import DateTime from '../signage-zone-types/dateTime.vue';
import Weather from '../signage-zone-types/weather.vue';
import ConnectionInfo from '../signage-zone-types/connectionInfo.vue';
import Image from '../signage-zone-types/image.vue';
import Text from '../signage-zone-types/text.vue';
import SignageLayoutPreview from "../signage-layouts/SignageLayoutPreview.vue";

// Interaces ---
import { ISignageLayout, ISignageZone } from '../../store/interfaces/ISignage';

// State ---
import SignageLayoutState, { ISignageLayoutState} from '../../store/modules/signageLayout';
import SignageListState, { ISignageListState} from '../../store/modules/signageList';
import { ISignageList } from '../../store/interfaces/ISignage';
import deepEqual from "deep-equal";
import { ElSelect } from "element-plus";
import SignageListAssignmentModal from './SignageListAssignmentModal.vue';

let signageLayoutState = {} as ISignageLayoutState;
let signageListState = {} as ISignageListState;

interface ISigangeZoneDisplay extends ISignageZone {
    allowedZoneTypes?: any[];
    selectedZoneTypes?: any[];
    allowScrolling?: boolean;
    zoneHeight?: number;
    zoneWidth?: number
}

export default defineComponent({
  name: 'SignageDetailsComponent',
  data: ()=>({
        signageLayout: {} as ISignageLayout, 
        originalSignageLayout: {} as ISignageLayout,
        sortedLayoutZones: [] as ISigangeZoneDisplay[],
        activeZones: [] as any,
        zonesWithChanges: [] as any,
 
        signageLayoutLoading: true as boolean,
        isEditingName: false as boolean,
        isPopoutPreviewVisible: false as boolean,
        isLightboxVisible: false as boolean,
        hasChanges: false as boolean,
        isInitialLoad: true as boolean,
        changeDetectionTimer: null as any,
        isSignageListAssignmentModalVisible: false as boolean,
        isAssigningSignageList: false as boolean,
        originalAssignedListIds: [] as string[],
        isLayoutReplacementWarningVisible: false as boolean,
        listsWithDifferentLayouts: [] as Array<{ list: ISignageList; layoutName: string }>,
        pendingSelectedLists: [] as ISignageList[],

        // Icons
        // ======
        mdiPencil,
        mdiOpenInNew,
        mdiFullscreen,
        mdiArrowUp,
        mdiRefresh,
        mdiAlertCircle
    }),
    
    computed: {
        allSignageLists(): ISignageList[] {
            return signageListState.allSignageLists || [];
        },
        
        assignedSignageLists(): ISignageList[] {
            const layoutId = this.$route.params.layoutid as string;
            if (!layoutId) return [];
            
            return signageListState.signageListsByLayoutId(layoutId);
        },
        
        currentlyAssignedListIds(): string[] {
            return this.originalAssignedListIds;
        }
    },

    components: {
        Icon,
        DraggableModal,
        Lightbox,
        dateTime: DateTime,
        weather: Weather,
        connectionInfo: ConnectionInfo,
        image: Image,
        text: Text,
        SignageLayoutPreview,
        SignageListAssignmentModal
    },

    methods: {
        handleBackgroundColorChange(color: string) {
            const finalColor = color || '#00766A';
            this.signageLayout.globalBackgroundColor = finalColor;
            this.updateSignageLayoutZones();
        },

        handleFontColorChange(color: string) {
            const finalColor = color || '#ffffff';
            this.signageLayout.globalFontColor = finalColor;
            this.updateSignageLayoutZones();
        },

        applyGlobalBackgroundColor(color: string) {
            this.signageLayout.globalBackgroundColor = color;
            this.sortedLayoutZones.forEach(zone => {
                zone.backgroundColor = color;
            });
            this.updateSignageLayoutZones();
            this.changeDetection();
        },

        applyGlobalFontColor(color: string) {
            this.sortedLayoutZones.forEach(zone => {
                zone.fontColor = color;
            });
            this.updateSignageLayoutZones();
            this.changeDetection();
        },
        handleZoneBoundaryColorChange(color: any) {
            if (!color || color === null) 
                this.signageLayout.zoneBoundaryColor = '#414141';
        },
        handleZoneBackgroundColorChange(color:any, zone: any) {
            if (!color || color === null) {
                zone.backgroundColor = this.getDefaultBackgroundColor();
            }
            this.updateSignageLayoutZones();
        },
        handleZoneFontColorChange(color:any, zone: any) {
            if (!color || color === null) {
                zone.fontColor = this.getDefaultFontColor();
            }
            this.updateSignageLayoutZones();
        },
        getDefaultBackgroundColor() {
            return this.signageLayout.globalBackgroundColor || '#00766A';
        },

        getDefaultFontColor() {
            return this.signageLayout.globalFontColor || '#ffffff';
        },

        updateSignageLayoutZones() {
            // Update the signageLayout.signageZones to reflect changes in the preview
            this.signageLayout.signageZones = this.sortedLayoutZones.map((zone: any) => {
                const cleanZone = { ...zone };
                delete cleanZone.allowedZoneTypes;
                delete cleanZone.selectedZoneTypes;
                delete cleanZone.allowScrolling;
                delete cleanZone.zoneHeight;
                delete cleanZone.zoneWidth;
                return cleanZone;
            });
        },
        toggleEditNameInput(){ 
            this.isEditingName = true;
            this.$nextTick(()=> {
                (this as any).$refs['editNameInput'].focus();
            });
        },
    
        async loadLayout() {
            this.signageLayoutLoading = true;
            this.isInitialLoad = true;
            const signageLayoutId = this.$route.params.layoutid as string;

            if(!signageLayoutId) {
                this.$router.push({ path: '/signage/layouts' });
                return;
            }
            const loadedLayout = JSON.parse(JSON.stringify(signageLayoutState.signageLayoutById(signageLayoutId)));
            if(!loadedLayout) {
                this.$router.push({ path: '/signage/layouts' });
                return
            }

            this.signageLayout = {
                ...loadedLayout,
                globalBackgroundColor: loadedLayout.globalBackgroundColor || '#00766A',
                globalFontColor: loadedLayout.globalFontColor || '#ffffff',
                zoneBoundaryColor: loadedLayout.zoneBoundaryColor || '#414141'
            };

            for(const zone of this.signageLayout.layout.zones) {
                let newZone: any;

                let exisitingZone;
       
                if(this.signageLayout.signageZones && this.signageLayout.signageZones.length > 0 ) {
                    exisitingZone = this.signageLayout.signageZones.find((x: any) => x.zoneNumber == zone.zoneNumber)
                }

                 if(exisitingZone) {
                     const zoneContent = exisitingZone.zoneContent || [];
                     const selectedZoneTypes = zoneContent.length > 0 ? zoneContent.map((x: any) => x.zoneType) : [];
                
                     newZone = {
                         ...exisitingZone,
                         selectedZoneTypes: selectedZoneTypes,
                         allowedZoneTypes: zone.allowedZoneTypes,
                         allowScrolling: zone.allowScrolling,
                         backgroundColor: exisitingZone.backgroundColor ? exisitingZone.backgroundColor : '#00766A',
                         fontColor: exisitingZone.fontColor ? exisitingZone.fontColor : '#ffffff',
                         zoneHeight: zone.height,
                         zoneWidth: zone.width,
                     } as ISigangeZoneDisplay;
                     
                 }
                else {
                    if(zone.zoneNumber == 1) {
                        newZone = {
                            zoneNumber: zone.zoneNumber,
                            scrollContent: false,
                            zoneContent: [{
                                zoneType: 'signageList',
                                content: {}
                            }],
                            selectedZoneTypes: ['signageList'],
                            allowedZoneTypes: zone.allowedZoneTypes,
                            allowScrolling: zone.allowScrolling,
                            zoneHeight: zone.height,
                            zoneWidth: zone.width
                        } as ISigangeZoneDisplay;
                    }
                    else {
                        newZone = {
                            zoneNumber: zone.zoneNumber,
                            scrollContent: false,
                            backgroundColor: '#00766A',
                            fontColor: '#ffffff',
                            zoneContent: [],
                            selectedZoneTypes: [],
                            allowedZoneTypes: zone.allowedZoneTypes,
                            allowScrolling: zone.allowScrolling,
                            zoneHeight: zone.height,
                            zoneWidth: zone.width
                        } as ISigangeZoneDisplay
                    }
                }

                this.sortedLayoutZones.push(newZone);
            }

             this.sortedLayoutZones = this.sortedLayoutZones.sort(function(a: any, b: any){return a.zoneNumber-b.zoneNumber});
                          
             this.signageLayoutLoading = false;

             this.$nextTick(() => {
                this.isInitialLoad = false;
            });
             
        },

        getZoneDisplayTitle(zone: any) {
            if (!zone || !zone.selectedZoneTypes || zone.selectedZoneTypes.length == 0) {
                return `Zone ${zone.zoneNumber}`;
            }

            const zoneTypes = zone.selectedZoneTypes.map((x: any) => this.getZoneTypeDisplayName(x));
            return `Zone ${zone.zoneNumber}: ${zoneTypes.join(', ')}`
        },

        getZoneTypeDisplayName(type: string) {
            if (!type) {
                return '';
            }

            switch (type) {
                case 'weather':
                    return 'Weather';
                case 'image':
                    return 'Image';
                case 'connectionInfo':
                    return 'Connection Information'
                case 'dateTime':
                    return 'Date and Time';
                case 'text':
                    return 'Text';
                case 'signageList':
                    return 'Signage Lists';
                default:
                    return type;
            }
        },

        getExistingContent(zone: ISignageZone, type: string) {
            return zone.zoneContent.find((x: any) => x.zoneType == type);
        },

        updateSignageZones(updates: any) {
            this.sortedLayoutZones.forEach((zone: any) => {
                if(zone.zoneNumber === updates.zoneNumber) {
                    const updatedContent = { zoneType: updates.zoneType, content: updates.updatedContent };

                    if(!zone.zoneContent.find((x:any) => x.zoneType == updatedContent.zoneType)) {
                        zone.zoneContent.push(updatedContent);
                    }
                    else {
                        zone.zoneContent.map((content: any) => { 
                            if(content.zoneType == updatedContent.zoneType) { 
                                content.content = updatedContent.content;
                            } 
                        })
                    }
                }
            });
            
            this.updateSignageLayoutZones();
        },

        async onReset(zone: any|null) {
            if(zone) {
                await (this as any).$confirm(`Undo all changes to zone ${zone.zoneNumber}?`, 'Warning', {
                    confirmButtonText: 'Undo Changes',
                    cancelButtonText: 'Cancel',
                    type: 'warning'
                });

                this.signageLayoutLoading = true;
               
                if (zone.zoneNumber !== 1) {
                    let resetZone = {} as ISigangeZoneDisplay;
                    const originalZone = this.originalSignageLayout.signageZones.find((x: any) => x.zoneNumber == zone.zoneNumber);
                    if(originalZone) {
                        resetZone = {
                            ...originalZone,
                            selectedZoneTypes: originalZone.zoneContent.map((x: any) => x.zoneType),
                            allowedZoneTypes: zone.allowedZoneTypes,
                            allowScrolling: zone.allowScrolling,
                            scrollContent: zone.allowScrolling ? true : false,
                            backgroundColor: originalZone.backgroundColor ? originalZone.backgroundColor : '#00766A',
                            fontColor: originalZone.fontColor ? originalZone.fontColor : '#ffffff',
                            zoneHeight: zone.zoneHeight,
                            zoneWidth: zone.zoneWidth,
                        } as ISigangeZoneDisplay;
                    }
                    else {
                        resetZone = {
                            zoneNumber: zone.zoneNumber,
                            scrollContent: false,
                            backgroundColor: '#00766A',
                            fontColor: '#ffffff',
                            zoneContent: [],
                            selectedZoneTypes: [],
                            allowedZoneTypes: zone.allowedZoneTypes,
                            allowScrolling: zone.allowScrolling,
                            zoneHeight: zone.zoneHeight,
                            zoneWidth: zone.zoneWidth
                        } as ISigangeZoneDisplay;
                    }
                    this.sortedLayoutZones = this.sortedLayoutZones.map((sortedZone: ISigangeZoneDisplay) => {
                        if(sortedZone.zoneNumber == zone.zoneNumber) {
                            return resetZone;
                        }
                        else {
                            return sortedZone;
                        }
                    })

                    this.updateSignageLayoutZones();
                }
                
                if (this.changeDetectionTimer) {
                    clearTimeout(this.changeDetectionTimer);
                    this.changeDetectionTimer = null;
                }
                
                this.$nextTick(() => {
                    this.changeDetection();
                    this.signageLayoutLoading = false;
                });
            }
            else {
                await (this as any).$confirm('Undo all changes to layout and all zones?', 'Warning', {
                    confirmButtonText: 'Undo Changes',
                    cancelButtonText: 'Cancel',
                    type: 'warning'
                });

                this.sortedLayoutZones = [];
                await signageLayoutState.fetchSignageLayouts(true);
                await signageListState.fetchSignageLists({ reload: true, includeRoomDetails: true });
                await this.loadLayout();
                const layoutId = this.$route.params.layoutid as string;
                const assigned = signageListState.signageListsByLayoutId(layoutId);
                this.originalAssignedListIds = assigned.map((list: ISignageList) => list.id).filter((id): id is string => !!id);
                PageDirtyService.markPageClean();
                this.hasChanges = false;
                this.zonesWithChanges = [];
            }
        },

        async onSubmit() {
            const signageLayoutId = this.$route.params.layoutid as string;
            // Do not save if any zones are empty
            const emptyZones = this.sortedLayoutZones.filter((z: any) => z.selectedZoneTypes.length == 0)
            if(emptyZones && emptyZones.length > 0) {
                const emptyZoneNumbers = emptyZones.map((x: any) => x.zoneNumber);
                let warningMessageNumbers = '';
                if(emptyZoneNumbers.length == 1) {
                    warningMessageNumbers = emptyZoneNumbers.join(', ');
                }
                else {
                    const last = emptyZoneNumbers.pop();
                    warningMessageNumbers = `${emptyZoneNumbers.join(', ')} and ${last}`;
                }

                await (this as any).$confirm(`Please select content for zone${emptyZoneNumbers.length == 1 ? '' : 's'} ${warningMessageNumbers}.`, 'Warning', {
                    confirmButtonText: 'Ok',
                    cancelButtonText: 'Cancel',
                    type: 'warning'
                });
                
                return;
            }


            // Do not save if there are no images in image zones
            const imageZones = this.sortedLayoutZones.filter((z: any) => z.selectedZoneTypes.includes('image'));
            const noImageZoneNumbers = [];
            for(const zone of imageZones) {
                if(zone.zoneContent.length === 0){
                    noImageZoneNumbers.push(zone.zoneNumber);
                    continue;
                }
                for(const content of zone.zoneContent) {
                    if(content.zoneType == 'image') {
                        if(!content.content.image && !content.content.imageFile) {
                            noImageZoneNumbers.push(zone.zoneNumber);
                        }
                    }
                }
            }
            if(noImageZoneNumbers.length > 0) {
                let warningMessageNumbers = '';
                if(noImageZoneNumbers.length == 1) {
                    warningMessageNumbers = noImageZoneNumbers.join(', ');
                }
                else {
                    const last = noImageZoneNumbers.pop();
                    warningMessageNumbers = `${noImageZoneNumbers.join(', ')} and ${last}`;
                }

                await (this as any).$confirm(`Please select an image for zone${noImageZoneNumbers.length == 1 ? '' : 's'} ${warningMessageNumbers}.`, 'Warning', {
                    confirmButtonText: 'Ok',
                    cancelButtonText: 'Cancel',
                    type: 'warning'
                });
                
                return;
            }

            this.signageLayoutLoading = true;

            //Upload any images before saving
            for(const zone of imageZones) {
                for(const content of zone.zoneContent) {
                    if(content.zoneType == 'image' && content.content.imageFile) {
                        const originalFileName = content.content.imageFile.name || 'image';
                        const sanitizedFileName = originalFileName.replace(/[^a-zA-Z0-9._-]/g, '_');
                        const uniqueFileName = `${signageLayoutId}_zone-${zone.zoneNumber}_${Date.now()}_${sanitizedFileName}`;
                        const digitalAsset = await digitalAssetUploadService.upload({
                            file: content.content.imageFile,
                            secure: false,
                            assetQuotaType: 1, //digital signage
                            generateThumb: false,
                            fileName: uniqueFileName,
                        })

                        if(digitalAsset && digitalAsset.assetFiles && digitalAsset.assetFiles[0]) {
                            content.content.image = { 
                                digitalAssetId:  digitalAsset.assetFiles[0].id,
                                url: digitalAsset.assetFiles[0].url
                            }
                        }
                        else {
                            (this as any).$message.error(`Failed to up load image for zone (${zone.zoneNumber})`);
                            this.signageLayoutLoading = false;
                            return;
                        }

                        content.content.imageFile = undefined;
                    }
                }
            }

            const cleanZonesForSaving = this.sortedLayoutZones.map(zone => {
                const cleanZone = { ...zone };
                delete cleanZone.allowedZoneTypes;
                delete cleanZone.selectedZoneTypes;
                delete cleanZone.allowScrolling;
                delete cleanZone.zoneHeight;
                delete cleanZone.zoneWidth;
                return cleanZone;
            });

            const payload = {
                signageLayoutId,
                name: this.signageLayout.name,
                zoneBoundaryColor: this.signageLayout.zoneBoundaryColor,
                globalBackgroundColor: this.signageLayout.globalBackgroundColor,
                globalFontColor: this.signageLayout.globalFontColor,
                signageZones: cleanZonesForSaving
            }
            
            const updateResponse = await signageLayoutState.updateSignageLayout(payload);

            if(!updateResponse.success) {
                (this as any).$message.error(updateResponse.reason ? `Signage layout update failed: ${updateResponse.reason}` : 'Signage layout update failed');
                this.signageLayoutLoading = false;
                return;
            }


            (this as any).$message.success('Signage layout updated');
            
            this.sortedLayoutZones = [];
            await Promise.all([
                signageLayoutState.fetchSignageLayouts(true),
                signageListState.fetchSignageLists(true, undefined, undefined, true)
            ]);
            await this.loadLayout();
            
            const layoutIdAfterSave = this.$route.params.layoutid as string;
            const assigned = signageListState.signageListsByLayoutId(layoutIdAfterSave);
            this.originalAssignedListIds = assigned.map((list: ISignageList) => list.id).filter((id): id is string => !!id);
            
            this.originalSignageLayout = signageLayoutState.signageLayoutById(signageLayoutId);
            PageDirtyService.markPageClean();
            this.hasChanges = false;
            this.signageLayoutLoading = false;
        },

        scrollToTop() {
            const divElement = document.getElementById("layoutPreview");
            if(divElement) {
                divElement.focus(); 
            }
        },

        updateZoneContent(zone: any) {
            const targetZone = this.sortedLayoutZones.find((z: any) => z.zoneNumber === zone.zoneNumber);
            if (!targetZone) return;

            if (targetZone.allowScrolling && targetZone.selectedZoneTypes?.includes('image') && targetZone.selectedZoneTypes.length > 1) {
                targetZone.selectedZoneTypes = ['image'];
            }
            
            targetZone.zoneContent = targetZone.zoneContent.filter((x: any) => targetZone.selectedZoneTypes && targetZone.selectedZoneTypes.includes(x.zoneType));
            
            if(targetZone.selectedZoneTypes && targetZone.selectedZoneTypes.length > 0) {
                targetZone.selectedZoneTypes.forEach((zoneType: string) => {
                    if(!targetZone.zoneContent.find((x: any) => x.zoneType === zoneType)) {
                        // Initialize content entry with default values for new zone type
                        let defaultContent: any = {};
                        
                        switch(zoneType) {
                            case 'weather':
                                defaultContent = {
                                    temperatureScale: 'Fahrenheit',
                                    location: {
                                        country: "United States of America",
                                        id: 2629508,
                                        lat: 40.88,
                                        lon: -81.4,
                                        name: "North Canton",
                                        region: "Ohio",
                                        url: "north-canton-ohio-united-states-of-america"
                                    }
                                };
                                break;
                            case 'dateTime':
                                defaultContent = {
                                    showDay: true,
                                    showDate: true,
                                    dateFormat: 'long',
                                    showTime: true,
                                    timeFormat24: false
                                };
                                break;
                            case 'text':
                                defaultContent = {
                                    text: ''
                                };
                                break;
                            case 'image':
                            case 'connectionInfo':
                            default:
                                defaultContent = {};
                                break;
                        }
                        
                        targetZone.zoneContent.push({
                            zoneType: zoneType,
                            content: defaultContent
                        });
                    }
                });
            }
            
            if (targetZone.allowScrolling) {
                const hasImage = targetZone.selectedZoneTypes?.includes('image');
                targetZone.scrollContent = !!(targetZone.selectedZoneTypes && targetZone.selectedZoneTypes.length > 0 && !hasImage);
            }

            // Create a new array with updated zone references to ensure reactivity
            this.sortedLayoutZones = this.sortedLayoutZones.map((z: any) => {
                if (z.zoneNumber === targetZone.zoneNumber) {
                    // Create a new zone object with new array references to ensure full reactivity
                    return { 
                        ...targetZone,
                        zoneContent: targetZone.zoneContent ? [...targetZone.zoneContent] : [],
                        selectedZoneTypes: targetZone.selectedZoneTypes ? [...targetZone.selectedZoneTypes] : []
                    };
                }
                return z;
            });
            
            // Update targetZone reference to point to the new zone object
            const updatedZone = this.sortedLayoutZones.find((z: any) => z.zoneNumber === targetZone.zoneNumber);
            
            this.updateSignageLayoutZones();

            this.$nextTick(() => {
                this.$forceUpdate();
                
                const selectRef = this.$refs[`zoneTypeSelect_${updatedZone?.zoneNumber || targetZone.zoneNumber}`] as InstanceType<typeof ElSelect> | undefined;

                if (selectRef && updatedZone) {
                    if (updatedZone.allowScrolling && updatedZone.selectedZoneTypes?.includes('image')) {
                        selectRef.blur();
                    }
                    else if (!updatedZone.allowScrolling && updatedZone.selectedZoneTypes && updatedZone.selectedZoneTypes.length === 1) {
                        selectRef.blur();
                    }
                    else if (updatedZone.allowScrolling && updatedZone.selectedZoneTypes && updatedZone.allowedZoneTypes && updatedZone.selectedZoneTypes.length === updatedZone.allowedZoneTypes.length) {
                        selectRef.blur();
                    }
                    else if (updatedZone.allowScrolling && updatedZone.selectedZoneTypes && updatedZone.allowedZoneTypes) {
                        const allowedNonImageCount = updatedZone.allowedZoneTypes.filter((type: string) => type !== 'image').length;
                        const hasImage = updatedZone.selectedZoneTypes.includes('image');
                        if (!hasImage && updatedZone.selectedZoneTypes.length === allowedNonImageCount) {
                            selectRef.blur();
                        }
                    }
                }
            });
            
        },

        isZoneTypeDisabled(zone: any, zoneType: string): boolean {
            if (!zone || !zone.allowScrolling) {
                return false;
            }

            const selected = zone.selectedZoneTypes || [];
            const hasImage = selected.includes('image');

            if (zoneType === 'image') {
                return selected.length > 0 && !hasImage;
            }

            return hasImage;
        },

        async changeDetection() {
            const signageLayoutId = this.$route.params.layoutid as string;
            if (!signageLayoutId) return;

            const originalFromStore = signageLayoutState.signageLayoutById(signageLayoutId);
            if (!originalFromStore) return;

            // Deep clone both objects to prevent reactive side effects
            const originalSignageLayout = JSON.parse(JSON.stringify(originalFromStore));
            const mutatedSignageLayout = JSON.parse(JSON.stringify(this.signageLayout));

            // Clean up unnecessary props for both
            const cleanZones = (zones: any[]) => {
                return zones.map(zone => {
                    const clone = { ...zone };
                    delete clone.allowedZoneTypes;
                    delete clone.selectedZoneTypes;
                    delete clone.allowScrolling;
                    return clone;
                });
            };

            originalSignageLayout.signageZones = cleanZones(originalSignageLayout.signageZones || []);
            mutatedSignageLayout.signageZones = cleanZones(mutatedSignageLayout.signageZones || []);

            this.zonesWithChanges = [];
            for (const zone of mutatedSignageLayout.signageZones) {
                const originalZone = originalSignageLayout.signageZones.find(
                    (x: any) => x.zoneNumber === zone.zoneNumber
                );
                // Skip zone 1 (list assignments) as those are saved immediately via modal
                if (!deepEqual(originalZone, zone) && zone.zoneNumber !== 1) {
                    this.zonesWithChanges.push(zone.zoneNumber);
                }
            }
            
            PageDirtyService.monitorObjectSet([
                { defaultObject: originalSignageLayout.name, mutatedObject: mutatedSignageLayout.name },
                { defaultObject: originalSignageLayout.zoneBoundaryColor, mutatedObject: mutatedSignageLayout.zoneBoundaryColor },
                { defaultObject: originalSignageLayout.globalBackgroundColor, mutatedObject: mutatedSignageLayout.globalBackgroundColor },
                { defaultObject: originalSignageLayout.globalFontColor, mutatedObject: mutatedSignageLayout.globalFontColor },
                { defaultObject: originalSignageLayout.signageZones, mutatedObject: mutatedSignageLayout.signageZones }
            ]);

            this.hasChanges = PageDirtyService.isPageDirty || this.zonesWithChanges.length > 0;
        },

        zoneFocus(zoneNumber: number) {
            const idx = this.activeZones.indexOf(zoneNumber);
            if(idx == -1) {
                this.activeZones = [zoneNumber];
            }
        
            // Use multiple nextTick calls to ensure DOM is fully updated
            this.$nextTick(() => {
                this.$nextTick(() => {
                    setTimeout(() => {
                        const zoneElement = document.getElementById(`editzone${zoneNumber}`);
                        if(zoneElement) {
                            zoneElement.setAttribute('tabindex', `${zoneNumber}`);
                            
                            // Get the actual top position and scroll there
                            const rect = zoneElement.getBoundingClientRect();
                            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                            const elementTop = rect.top + scrollTop;
                            
                            // Scroll to position with some top padding
                            window.scrollTo({
                                top: elementTop - 80,
                                behavior: 'smooth'
                            });
                            
                            zoneElement.focus();
                        }
                    }, 200); 
                });
            });
        },
        
        toggleAssignSignageList() {
            this.isSignageListAssignmentModalVisible = true;
        },
        
        async onSignageListAssigned(selectedLists: ISignageList[]) {
            try {
                this.isAssigningSignageList = true;
                const layoutId = this.$route.params.layoutid as string;
                
                if (selectedLists && selectedLists.length > 0) {
                    const listsWithDifferentLayouts: Array<{ list: ISignageList; layoutName: string }> = [];
                    
                    for (const selectedList of selectedLists) {
                        if (selectedList.id) {
                            const listFromStore = signageListState.signageListById(selectedList.id);
                            if (listFromStore) {
                                const previousLayoutId = listFromStore.signageLayout?.id;
                                if (previousLayoutId && previousLayoutId !== layoutId) {
                                    const previousLayout = signageLayoutState.signageLayoutById(previousLayoutId);
                                    const previousLayoutName = previousLayout?.name || 'another layout';
                                    listsWithDifferentLayouts.push({
                                        list: selectedList,
                                        layoutName: previousLayoutName
                                    });
                                }
                            }
                        }
                    }
                    
                    if (listsWithDifferentLayouts.length > 0) {
                        if (listsWithDifferentLayouts.length === 1) {
                            // Single list - use simple confirm dialog
                            const listName = listsWithDifferentLayouts[0].list.name;
                            const message = `This signage list "${listName}" already has a layout. Assigning a new layout will replace the existing layout. Continue?`;
                            
                            try {
                                await (this as any).$confirm(
                                    message,
                                    'Warning',
                                    {
                                        confirmButtonText: 'Yes',
                                        cancelButtonText: 'Cancel',
                                        type: 'warning'
                                    }
                                );
                            } catch (confirmError) {
                                // Don't close assignment modal on cancel - let user explicitly close it
                                this.isAssigningSignageList = false;
                                return;
                            }
                        } else {
                            // Multiple lists - show modal dialog
                            this.listsWithDifferentLayouts = listsWithDifferentLayouts;
                            this.pendingSelectedLists = selectedLists;
                            this.isLayoutReplacementWarningVisible = true;
                            this.isAssigningSignageList = false;
                            // Don't close assignment modal - keep it open behind the warning
                            return;
                        }
                    }
                    
                    // Save the list assignments
                    const selectedIds = selectedLists.map(list => list.id).filter((id): id is string => !!id);
                    const originalIds = this.originalAssignedListIds || [];
                    
                    const listsToRemove = originalIds.filter(id => !selectedIds.includes(id));
                    const listsToAdd = selectedIds.filter(id => !originalIds.includes(id));
                    
                    for (const listId of listsToRemove) {
                        const listFromStore = signageListState.signageListById(listId);
                        if (listFromStore) {
                            const listToUpdate = JSON.parse(JSON.stringify(listFromStore)) as ISignageList;
                            listToUpdate.signageLayout = null as any;
                            const listResponse = await signageListState.updateSignageList(listToUpdate);
                            if (!listResponse.success) {
                                (this as any).$message.error(listResponse.reason || `Failed to remove signage list assignment`);
                                this.isAssigningSignageList = false;
                                return;
                            }
                        }
                    }
                    
                    for (const listId of listsToAdd) {
                        const listFromStore = signageListState.signageListById(listId);
                        if (listFromStore) {
                            const listToUpdate = JSON.parse(JSON.stringify(listFromStore)) as ISignageList;
                            listToUpdate.signageLayout = {
                                id: layoutId,
                                type: 'SignageLayout',
                                name: this.signageLayout.name
                            } as any;
                            
                            const listResponse = await signageListState.updateSignageList(listToUpdate);
                            if (!listResponse.success) {
                                (this as any).$message.error(listResponse.reason || `Failed to assign signage list`);
                                this.isAssigningSignageList = false;
                                return;
                            }
                        }
                    }
                    
                    // Refresh the lists and update originalAssignedListIds
                    await signageListState.fetchSignageLists({ reload: true, includeRoomDetails: true });
                    const assigned = signageListState.signageListsByLayoutId(layoutId);
                    this.originalAssignedListIds = assigned.map((list: ISignageList) => list.id).filter((id): id is string => !!id);
                    
                    (this as any).$message.success('Signage lists assigned successfully');
                    this.isSignageListAssignmentModalVisible = false;
                } else {
                    // Remove all assignments
                    const originalIds = this.originalAssignedListIds || [];
                    
                    for (const listId of originalIds) {
                        const listFromStore = signageListState.signageListById(listId);
                        if (listFromStore) {
                            const listToUpdate = JSON.parse(JSON.stringify(listFromStore)) as ISignageList;
                            listToUpdate.signageLayout = null as any;
                            const listResponse = await signageListState.updateSignageList(listToUpdate);
                            if (!listResponse.success) {
                                (this as any).$message.error(listResponse.reason || `Failed to remove signage list assignment`);
                                this.isAssigningSignageList = false;
                                return;
                            }
                        }
                    }
                    
                    // Refresh the lists and update originalAssignedListIds
                    await signageListState.fetchSignageLists({ reload: true, includeRoomDetails: true });
                    const assigned = signageListState.signageListsByLayoutId(layoutId);
                    this.originalAssignedListIds = assigned.map((list: ISignageList) => list.id).filter((id): id is string => !!id);
                    
                    (this as any).$message.success('Signage lists removed successfully');
                    this.isSignageListAssignmentModalVisible = false;
                }
                
                this.isAssigningSignageList = false;
            } catch (error: any) {
                this.isAssigningSignageList = false;
                if (error !== 'cancel') {
                    console.error('Error assigning signage lists:', error);
                    (this as any).$message.error('An error occurred while assigning the signage lists');
                }
            }
        },

        onCancelLayoutReplacement() {
            this.isLayoutReplacementWarningVisible = false;
            this.listsWithDifferentLayouts = [];
            this.pendingSelectedLists = [];
        },

        async onConfirmLayoutReplacement() {
            this.isLayoutReplacementWarningVisible = false;
            this.isAssigningSignageList = true;
            
            try {
                const layoutId = this.$route.params.layoutid as string;
                const selectedIds = this.pendingSelectedLists.map(list => list.id).filter((id): id is string => !!id);
                const originalIds = this.originalAssignedListIds || [];
                
                const listsToRemove = originalIds.filter(id => !selectedIds.includes(id));
                const listsToAdd = selectedIds.filter(id => !originalIds.includes(id));
                
                for (const listId of listsToRemove) {
                    const listFromStore = signageListState.signageListById(listId);
                    if (listFromStore) {
                        const listToUpdate = JSON.parse(JSON.stringify(listFromStore)) as ISignageList;
                        listToUpdate.signageLayout = null as any;
                        const listResponse = await signageListState.updateSignageList(listToUpdate);
                        if (!listResponse.success) {
                            (this as any).$message.error(listResponse.reason || `Failed to remove signage list assignment`);
                            this.isAssigningSignageList = false;
                            return;
                        }
                    }
                }
                
                for (const listId of listsToAdd) {
                    const listFromStore = signageListState.signageListById(listId);
                    if (listFromStore) {
                        const listToUpdate = JSON.parse(JSON.stringify(listFromStore)) as ISignageList;
                        listToUpdate.signageLayout = {
                            id: layoutId,
                            type: 'SignageLayout',
                            name: this.signageLayout.name
                        } as any;
                        
                        const listResponse = await signageListState.updateSignageList(listToUpdate);
                        if (!listResponse.success) {
                            (this as any).$message.error(listResponse.reason || `Failed to assign signage list`);
                            this.isAssigningSignageList = false;
                            return;
                        }
                    }
                }
                
                // Refresh the lists and update originalAssignedListIds
                await signageListState.fetchSignageLists({ reload: true, includeRoomDetails: true });
                const assigned = signageListState.signageListsByLayoutId(layoutId);
                this.originalAssignedListIds = assigned.map((list: ISignageList) => list.id).filter((id): id is string => !!id);
                
                (this as any).$message.success('Signage lists assigned successfully');
                this.isSignageListAssignmentModalVisible = false;
            } catch (error: any) {
                console.error('Error assigning signage lists:', error);
                (this as any).$message.error('An error occurred while assigning the signage lists');
            } finally {
                this.isAssigningSignageList = false;
                this.listsWithDifferentLayouts = [];
                this.pendingSelectedLists = [];
            }
        }

    },

    inject: [
        'windowWidth'
    ],


    async created() {
        signageLayoutState = getModule(SignageLayoutState);
        signageListState = getModule(SignageListState);

        await Promise.all([
            signageLayoutState.fetchSignageLayouts(true),
            signageListState.fetchSignageLists({ reload: true, includeRoomDetails: true }),
        ]);

        await this.loadLayout();
        this.activeZones = [1];
        const signageLayoutId = this.$route.params.layoutid as string;
        this.originalSignageLayout = signageLayoutState.signageLayoutById(signageLayoutId);
        
        const assigned = signageListState.signageListsByLayoutId(signageLayoutId);
        this.originalAssignedListIds = assigned.map((list: ISignageList) => list.id).filter((id): id is string => !!id);
        
        PageDirtyService.markPageClean();
    },

    beforeUnmount() {
        if (this.changeDetectionTimer) {
            clearTimeout(this.changeDetectionTimer);
            this.changeDetectionTimer = null;
        }
    },

    watch: {
        'signageLayout.name': {
            handler(){
                this.changeDetection();
            },
            immediate: false
        },

        'signageLayout.zoneBoundaryColor': {
            async handler(){
                this.changeDetection();
            },
            immediate: false
        },

        'signageLayout.signageZones': {
            handler(){
                // Debounce change detection to prevent UI blocking during rapid updates
                if (this.changeDetectionTimer) {
                    clearTimeout(this.changeDetectionTimer);
                }
                this.changeDetectionTimer = setTimeout(() => {
                    this.changeDetection();
                    this.changeDetectionTimer = null;
                }, 100);
            },
            deep: true,
            immediate: false
        },
        'signageLayout.globalBackgroundColor': {
        handler(newBgColor) {
            if(!newBgColor || newBgColor === null) 
                newBgColor = '#00766A';
            //if initial load, apply specific color else apply global bg to all zones
            if (this.isInitialLoad) {
                this.sortedLayoutZones.forEach(zone => {
                    if (!zone.backgroundColor || zone.backgroundColor === '#00766A') {
                        zone.backgroundColor = newBgColor;
                    }
                });
            } else {
                this.applyGlobalBackgroundColor(newBgColor);
            }
        },
        immediate: true
    },
    
    'signageLayout.globalFontColor': {
        handler(newFontColor) {
            // Only apply to zones during initial load if they don't have a specific color
            if (this.isInitialLoad) {
                this.sortedLayoutZones.forEach(zone => {
                    if (!zone.fontColor || zone.fontColor === '#ffffff') {
                        zone.fontColor = newFontColor;
                    }
                });
            } else {
                // When manually changed, apply to all zones
                this.applyGlobalFontColor(newFontColor);
            }
        },
        immediate: true
    }
    }
});
